import React, { useState, useEffect } from 'react';
import {
  getUsuarios,
  crearUsuario,
  actualizarUsuario,
  eliminarUsuario,
  getSedesActivas,
  getSectores,
  getSedesAdmin,
  asignarSedesAdmin,
  getSedes,
} from '../../services/api';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'apellido', direccion: 'asc' });
  const [sedes, setSedes] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [formData, setFormData] = useState({
    dni: '',
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    tipo_usuario: 'operario',
    sede_id: '',
    sector_id: '',
  });
  const [mensaje, setMensaje] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSedesModal, setShowSedesModal] = useState(false);
  const [adminSeleccionado, setAdminSeleccionado] = useState(null);
  const [todasLasSedes, setTodasLasSedes] = useState([]);
  const [sedesAsignadas, setSedesAsignadas] = useState([]);

  const mostrarMensaje = (texto, tipo) => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 5000);
  };

  const cargarSedes = async () => {
    try {
      const res = await getSedesActivas();
      setSedes(res.data);
    } catch (error) {
      console.error('Error cargando sedes:', error);
      mostrarMensaje('Error al cargar sedes', 'error');
    }
  };

  const cargarSectores = async (sedeId) => {
    if (!sedeId) {
      setSectores([]);
      return;
    }
    try {
      const res = await getSectores({ sedeId });
      setSectores(res.data);
    } catch (error) {
      console.error('Error cargando sectores:', error);
      mostrarMensaje('Error al cargar sectores', 'error');
    }
  };

  const cargarUsuarios = async () => {
    try {
      const params = {};
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroActivo) params.activo = filtroActivo;

      const response = await getUsuarios(params);
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      mostrarMensaje('Error al cargar usuarios', 'error');
    }
  };

  useEffect(() => {
    cargarSedes();
    cargarTodasLasSedes();
    cargarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarTodasLasSedes = async () => {
    try {
      const res = await getSedes();
      setTodasLasSedes(res.data);
    } catch (error) {
      console.error('Error cargando todas las sedes:', error);
    }
  };

  const abrirModalSedes = async (admin) => {
    setAdminSeleccionado(admin);
    try {
      const res = await getSedesAdmin(admin.id);
      setSedesAsignadas(res.data.map(s => s.id));
    } catch (error) {
      console.error('Error cargando sedes del admin:', error);
      setSedesAsignadas([]);
    }
    setShowSedesModal(true);
  };

  const cerrarModalSedes = () => {
    setShowSedesModal(false);
    setAdminSeleccionado(null);
    setSedesAsignadas([]);
  };

  const toggleSedeAsignada = (sedeId) => {
    setSedesAsignadas(prev => {
      if (prev.includes(sedeId)) {
        return prev.filter(id => id !== sedeId);
      }
      return [...prev, sedeId];
    });
  };

  const guardarSedesAsignadas = async () => {
    if (!adminSeleccionado) return;

    setLoading(true);
    try {
      await asignarSedesAdmin(adminSeleccionado.id, sedesAsignadas);
      mostrarMensaje('Sedes asignadas exitosamente', 'success');
      cerrarModalSedes();
      cargarUsuarios();
    } catch (error) {
      console.error('Error asignando sedes:', error);
      mostrarMensaje(
        error.response?.data?.error || 'Error al asignar sedes',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTipo, filtroActivo]);

  const abrirModal = async (usuario = null) => {
    if (usuario) {
      setEditando(usuario.id);
      setFormData({
        dni: usuario.dni,
        email: usuario.email || '',
        password: '',  // Nunca pre-llenar password
        nombre: usuario.nombre,
        apellido: usuario.apellido,
        tipo_usuario: usuario.tipo_usuario,
        sede_id: usuario.sede_id || '',
        sector_id: usuario.sector_id || '',
      });
      // Cargar sectores de la sede del usuario
      if (usuario.sede_id) {
        await cargarSectores(usuario.sede_id);
      }
    } else {
      setEditando(null);
      setFormData({
        dni: '',
        email: '',
        password: '',
        nombre: '',
        apellido: '',
        tipo_usuario: 'operario',
        sede_id: '',
        sector_id: '',
      });
      setSectores([]);
    }
    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setFormData({
      dni: '',
      email: '',
      password: '',
      nombre: '',
      apellido: '',
      tipo_usuario: 'operario',
      sede_id: '',
      sector_id: '',
    });
    setSectores([]);
  };

  const handleSedeChange = async (sedeId) => {
    setFormData({ ...formData, sede_id: sedeId, sector_id: '' });
    await cargarSectores(sedeId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editando) {
        await actualizarUsuario(editando, formData);
        mostrarMensaje('Usuario actualizado exitosamente', 'success');
      } else {
        await crearUsuario(formData);
        mostrarMensaje('Usuario creado exitosamente', 'success');
      }
      cerrarModal();
      cargarUsuarios();
    } catch (error) {
      console.error('Error guardando usuario:', error);
      mostrarMensaje(
        error.response?.data?.error || 'Error al guardar usuario',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id, nombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${nombre}"?`)) {
      return;
    }

    try {
      await eliminarUsuario(id);
      mostrarMensaje('Usuario eliminado exitosamente', 'success');
      cargarUsuarios();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      mostrarMensaje(
        error.response?.data?.error || 'Error al eliminar usuario',
        'error'
      );
    }
  };

  const toggleActivo = async (usuario) => {
    try {
      await actualizarUsuario(usuario.id, { activo: !usuario.activo });
      cargarUsuarios();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      mostrarMensaje('Error al actualizar estado', 'error');
    }
  };

  // Filtrar y ordenar usuarios
  const usuariosFiltrados = usuarios
    .filter((usuario) => {
      const searchTerm = busqueda.toLowerCase();
      return (
        usuario.apellido.toLowerCase().includes(searchTerm) ||
        usuario.nombre.toLowerCase().includes(searchTerm) ||
        usuario.dni.includes(searchTerm) ||
        (usuario.email && usuario.email.toLowerCase().includes(searchTerm))
      );
    })
    .sort((a, b) => {
      const { campo, direccion } = ordenamiento;
      let valorA = a[campo];
      let valorB = b[campo];

      // Convertir a minúsculas para ordenamiento de texto
      if (typeof valorA === 'string') valorA = valorA.toLowerCase();
      if (typeof valorB === 'string') valorB = valorB.toLowerCase();

      if (valorA < valorB) return direccion === 'asc' ? -1 : 1;
      if (valorA > valorB) return direccion === 'asc' ? 1 : -1;
      return 0;
    });

  const handleOrdenar = (campo) => {
    setOrdenamiento((prev) => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'asc' ? 'desc' : 'asc',
    }));
  };

  const estadisticas = {
    total: usuarios.length,
    operarios: usuarios.filter((u) => u.tipo_usuario === 'operario').length,
    oficina: usuarios.filter((u) => u.tipo_usuario === 'oficina').length,
    encargados: usuarios.filter((u) => u.tipo_usuario === 'encargado').length,
    admin: usuarios.filter((u) => u.tipo_usuario === 'admin').length,
    activos: usuarios.filter((u) => u.activo).length,
  };

  return (
    <div>
      <div className="page-header">
        <h1>Gestión de Usuarios</h1>
        <p>Administra los usuarios del sistema</p>
      </div>

      {mensaje && (
        <div className={`alert alert-${mensaje.tipo}`}>{mensaje.texto}</div>
      )}

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{estadisticas.total}</h3>
          <p>Total Usuarios</p>
        </div>
        <div className="stat-card">
          <h3>{estadisticas.operarios}</h3>
          <p>Operarios</p>
        </div>
        <div className="stat-card">
          <h3>{estadisticas.oficina}</h3>
          <p>Oficina</p>
        </div>
        <div className="stat-card">
          <h3>{estadisticas.encargados}</h3>
          <p>Encargados</p>
        </div>
        <div className="stat-card">
          <h3>{estadisticas.activos}</h3>
          <p>Activos</p>
        </div>
      </div>

      <div className="card">
        {/* Filtros y búsqueda */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px',
            gap: '15px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: '15px', flex: 1, flexWrap: 'wrap' }}>
            {/* Búsqueda */}
            <div className="form-group" style={{ marginBottom: 0, minWidth: '250px', flex: 1 }}>
              <label>🔍 Buscar por DNI, Email o Apellido</label>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>

            {/* Filtro tipo */}
            <div className="form-group" style={{ marginBottom: 0, minWidth: '150px' }}>
              <label>Tipo de Usuario</label>
              <select
                className="form-control"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="operario">Operarios</option>
                <option value="oficina">Oficina</option>
                <option value="encargado">Encargados</option>
                <option value="admin">Administradores</option>
              </select>
            </div>

            {/* Filtro estado */}
            <div className="form-group" style={{ marginBottom: 0, minWidth: '150px' }}>
              <label>Estado</label>
              <select
                className="form-control"
                value={filtroActivo}
                onChange={(e) => setFiltroActivo(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={() => abrirModal()}
            style={{ alignSelf: 'flex-end' }}
          >
            + Nuevo Usuario
          </button>
        </div>

        {/* Resultados de búsqueda */}
        {busqueda && (
          <div style={{ marginBottom: '10px', color: '#7f8c8d', fontSize: '14px' }}>
            Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
          </div>
        )}

        {/* Tabla de usuarios - DataGrid style */}
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px', position: 'relative' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f8f9fa' }}>
              <tr style={{ background: '#f8f9fa' }}>
                <th
                  onClick={() => handleOrdenar('dni')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  DNI {ordenamiento.campo === 'dni' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleOrdenar('email')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Email {ordenamiento.campo === 'email' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleOrdenar('nombre')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Nombre {ordenamiento.campo === 'nombre' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleOrdenar('apellido')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Apellido {ordenamiento.campo === 'apellido' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleOrdenar('tipo_usuario')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Tipo {ordenamiento.campo === 'tipo_usuario' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th>Sede</th>
                <th>Sector</th>
                <th
                  onClick={() => handleOrdenar('activo')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Estado {ordenamiento.campo === 'activo' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleOrdenar('created_at')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Fecha Creación {ordenamiento.campo === 'created_at' && (ordenamiento.direccion === 'asc' ? '↑' : '↓')}
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                    {busqueda ? 'No se encontraron usuarios con ese criterio' : 'No hay usuarios para mostrar'}
                  </td>
                </tr>
              ) : (
                usuariosFiltrados.map((usuario) => (
                <tr key={usuario.id}>
                  <td><strong>{usuario.dni}</strong></td>
                  <td>
                    {usuario.email ? (
                      <span style={{ fontSize: '13px' }}>{usuario.email}</span>
                    ) : (
                      <small className="text-muted">Sin email</small>
                    )}
                  </td>
                  <td>{usuario.nombre}</td>
                  <td>{usuario.apellido}</td>
                  <td>
                    <span
                      style={{
                        fontSize: '12px',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        background:
                          usuario.tipo_usuario === 'admin'
                            ? '#e74c3c'
                            : usuario.tipo_usuario === 'oficina'
                            ? '#3498db'
                            : '#27ae60',
                        color: 'white',
                        fontWeight: '500',
                      }}
                    >
                      {usuario.tipo_usuario}
                    </span>
                  </td>
                  <td>
                    {usuario.sede_nombre ? (
                      <small className="text-muted">
                        {usuario.sede_nombre} ({usuario.sede_codigo})
                      </small>
                    ) : (
                      <small className="text-muted">-</small>
                    )}
                  </td>
                  <td>
                    {usuario.sector_nombre ? (
                      <small className="text-muted">
                        {usuario.sector_nombre}
                      </small>
                    ) : (
                      <small className="text-muted">-</small>
                    )}
                  </td>
                  <td>
                    <button
                      className={`btn ${
                        usuario.activo ? 'btn-success' : 'btn-secondary'
                      }`}
                      onClick={() => toggleActivo(usuario)}
                      style={{ fontSize: '12px', padding: '5px 10px' }}
                    >
                      {usuario.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td>
                    {new Date(usuario.created_at).toLocaleDateString('es-AR')}
                  </td>
                  <td>
                    <button
                      className="btn btn-primary"
                      onClick={() => abrirModal(usuario)}
                      style={{
                        fontSize: '12px',
                        padding: '5px 10px',
                        marginRight: '5px',
                      }}
                    >
                      Editar
                    </button>
                    {usuario.tipo_usuario === 'admin' && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => abrirModalSedes(usuario)}
                        style={{
                          fontSize: '12px',
                          padding: '5px 10px',
                          marginRight: '5px',
                        }}
                        title="Asignar sedes a este administrador"
                      >
                        Sedes
                      </button>
                    )}
                    {usuario.tipo_usuario !== 'admin' && (
                      <button
                        className="btn btn-danger"
                        onClick={() =>
                          handleEliminar(
                            usuario.id,
                            `${usuario.nombre} ${usuario.apellido}`
                          )
                        }
                        style={{ fontSize: '12px', padding: '5px 10px' }}
                      >
                        Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>DNI *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.dni}
                  onChange={(e) =>
                    setFormData({ ...formData, dni: e.target.value })
                  }
                  required
                  disabled={editando !== null}
                  placeholder="Sin puntos ni guiones"
                />
                {editando && (
                  <small style={{ color: '#7f8c8d', fontSize: '12px' }}>
                    El DNI no se puede modificar
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Email {!editando && '*'}</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="correo@ejemplo.com"
                  required={!editando}
                />
                <small className="text-muted">
                  Requerido para usuarios que usen la aplicación web
                </small>
              </div>

              <div className="form-group">
                <label>Contraseña {!editando && '*'}</label>
                <input
                  type="password"
                  className="form-control"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder={editando ? 'Dejar vacío para no cambiar' : 'Ingresa una contraseña'}
                  required={!editando}
                />
                {editando && (
                  <small style={{ color: '#7f8c8d', fontSize: '12px' }}>
                    Dejar vacío si no deseas cambiar la contraseña
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Apellido *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.apellido}
                  onChange={(e) =>
                    setFormData({ ...formData, apellido: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Tipo de Usuario *</label>
                <select
                  className="form-control"
                  value={formData.tipo_usuario}
                  onChange={(e) =>
                    setFormData({ ...formData, tipo_usuario: e.target.value })
                  }
                  required
                >
                  <option value="operario">Operario (usa el totem)</option>
                  <option value="oficina">Oficina (usa la web)</option>
                  <option value="encargado">Encargado (puede pedir en cantidad)</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              <div className="form-group">
                <label>Sede</label>
                <select
                  className="form-control"
                  value={formData.sede_id}
                  onChange={(e) => handleSedeChange(e.target.value)}
                >
                  <option value="">Sin sede asignada</option>
                  {sedes.map((sede) => (
                    <option key={sede.id} value={sede.id}>
                      {sede.nombre} ({sede.codigo})
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  La sede determina qué menú puede ver el usuario
                </small>
              </div>

              <div className="form-group">
                <label>Sector</label>
                <select
                  className="form-control"
                  value={formData.sector_id}
                  onChange={(e) =>
                    setFormData({ ...formData, sector_id: e.target.value })
                  }
                  disabled={!formData.sede_id || sectores.length === 0}
                >
                  <option value="">Sin sector asignado</option>
                  {sectores.map((sector) => (
                    <option key={sector.id} value={sector.id}>
                      {sector.nombre} ({sector.codigo})
                    </option>
                  ))}
                </select>
                {!formData.sede_id && (
                  <small className="text-muted">
                    Primero selecciona una sede
                  </small>
                )}
                {formData.sede_id && sectores.length === 0 && (
                  <small className="text-muted">
                    No hay sectores disponibles para esta sede
                  </small>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cerrarModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? 'Guardando...'
                    : editando
                    ? 'Actualizar'
                    : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Asignación de Sedes */}
      {showSedesModal && adminSeleccionado && (
        <div className="modal-overlay" onClick={cerrarModalSedes}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>Asignar Sedes a {adminSeleccionado.nombre} {adminSeleccionado.apellido}</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Selecciona las sedes que este administrador puede gestionar.
              Los super_admin tienen acceso a todas las sedes automáticamente.
            </p>

            <div style={{ marginBottom: '20px' }}>
              {todasLasSedes.length === 0 ? (
                <p style={{ color: '#7f8c8d', textAlign: 'center' }}>
                  No hay sedes disponibles
                </p>
              ) : (
                todasLasSedes.map((sede) => (
                  <div
                    key={sede.id}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background: sedesAsignadas.includes(sede.id) ? '#e8f5e9' : 'white',
                    }}
                    onClick={() => toggleSedeAsignada(sede.id)}
                  >
                    <input
                      type="checkbox"
                      checked={sedesAsignadas.includes(sede.id)}
                      onChange={() => toggleSedeAsignada(sede.id)}
                      style={{ marginRight: '12px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <strong>{sede.nombre}</strong>
                      <span style={{ color: '#7f8c8d', marginLeft: '8px' }}>
                        ({sede.codigo})
                      </span>
                      {!sede.activo && (
                        <span style={{ color: '#e74c3c', marginLeft: '8px', fontSize: '12px' }}>
                          (Inactiva)
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <strong>Sedes seleccionadas:</strong> {sedesAsignadas.length}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cerrarModalSedes}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={guardarSedesAsignadas}
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Asignación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Usuarios;
